import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Drag and drop for the collection page's studies (reorder within a band, move
 * into another group, or out of every group into the ungrouped band) and for
 * the viewer's image navigator (reorder within one group).
 *
 * Pointer events rather than HTML5 drag and drop, so the page can show the
 * drop before it happens: the studies being carried are drawn, faded, where
 * they would land (the page re-lays itself out around them), and a card
 * follows the pointer. Nothing is written until the pointer is released.
 *
 * The DOM contract, kept by the page:
 *  - each band carries `data-band` (a group's public id, or 'root');
 *  - each study carries `data-sid` (its public id), and `data-ghost` while it
 *    is one of the studies being carried;
 *  - `data-layout="list"` on a band whose studies are rows (top/bottom halves
 *    decide before/after) rather than a grid (left/right halves);
 *  - `data-drag-width` on a study whose floating card is not the study's own
 *    width (a list row becomes a card of that width).
 */

/** Where a drop would land: band, and the shown study it goes before (null: the end). */
export interface DropTarget {
  band: string;
  beforeId: string | null;
}

export interface DragState {
  /** The studies being carried, in page order; `lead` is the one grabbed. */
  ids: string[];
  lead: string;
  x: number;
  y: number;
  /** Where the pointer holds the floating card, from its top left. */
  offsetX: number;
  offsetY: number;
  width: number;
  target: DropTarget | null;
  /** The one band this drag may land in, when it is confined (`lockBand`). */
  lock?: string;
}

const MOUSE_SLOP = 5;
const TOUCH_HOLD_MS = 300;
const TOUCH_SLOP = 8;
const EDGE = 72;
const NO_DRAG = 'button, a, input, textarea, select, [role="checkbox"], [data-nodrag]';

interface Box { left: number; top: number; right: number; bottom: number }

/**
 * An element's box where the layout puts it, not where an animation is showing
 * it: hit testing against positions in motion made the drop point flicker as
 * the cards it was measured against slid past.
 */
function layoutBox(el: Element): Box {
  const r = el.getBoundingClientRect();
  const t = getComputedStyle(el).transform;
  if (!t || t === 'none') return r;
  const m = new DOMMatrixReadOnly(t);
  return { left: r.left - m.m41, top: r.top - m.m42, right: r.right - m.m41, bottom: r.bottom - m.m42 };
}

const inside = (b: Box, x: number, y: number) => x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;

/**
 * The drop target under (x, y); `prev` is kept wherever the answer would be
 * ambiguous. With `lock`, only that band is a candidate: anywhere else the
 * answer is null, so the outline drops and a release there changes nothing.
 */
function hitTest(x: number, y: number, prev: DropTarget | null, lock?: string): DropTarget | null {
  const bands = [...document.querySelectorAll<HTMLElement>('[data-band]')].filter((b) => lock === undefined || b.dataset.band === lock);
  let band = bands.find((b) => inside(b.getBoundingClientRect(), x, y));
  if (!band) {
    // The gap between two bands belongs to the nearer, within reach.
    let best = 36;
    for (const b of bands) {
      const r = b.getBoundingClientRect();
      if (x < r.left || x > r.right) continue;
      const d = y < r.top ? r.top - y : y - r.bottom;
      if (d < best) { best = d; band = b; }
    }
  }
  if (!band) return null;
  const key = band.dataset.band!;
  const list = band.dataset.layout === 'list';
  const items = [...band.querySelectorAll<HTMLElement>('[data-sid]')].map((el) => ({ el, box: layoutBox(el), ghost: el.dataset.ghost != null }));
  const over = items.find((i) => inside(i.box, x, y));
  // Over the carried studies themselves: they are already where the pointer
  // says, so nothing changes (and, on the first move, nothing is decided yet).
  if (over?.ghost) return prev;
  const solid = items.filter((i) => !i.ghost);
  if (over) {
    const b = over.box;
    const after = list ? y > (b.top + b.bottom) / 2 : x > (b.left + b.right) / 2;
    if (!after) return { band: key, beforeId: over.el.dataset.sid! };
    const next = solid[solid.indexOf(over) + 1];
    return { band: key, beforeId: next ? next.el.dataset.sid! : null };
  }
  // In a gap, above or below the studies: the first one the pointer is before,
  // reading left to right and top to bottom.
  const first = solid.find((i) => (list ? (i.box.top + i.box.bottom) / 2 > y : i.box.top > y || (y <= i.box.bottom && (i.box.left + i.box.right) / 2 > x)));
  return { band: key, beforeId: first ? first.el.dataset.sid! : null };
}

const sameTarget = (a: DropTarget | null, b: DropTarget | null) => a === b || (!!a && !!b && a.band === b.band && a.beforeId === b.beforeId);

export function useStudyDrag({
  enabled,
  carry,
  onDrop,
  scroller,
  lockBand,
}: {
  enabled: boolean;
  /** The studies a grab of `id` carries: the whole selection when it is ticked. */
  carry: (id: string) => string[];
  onDrop: (ids: string[], target: DropTarget) => void;
  /**
   * The element the bands scroll inside, when it is not the page: the edge
   * autoscroll works its box instead of the window's.
   */
  scroller?: HTMLElement | null;
  /** The only band a grab of `id` may land in: the drag never leaves it. */
  lockBand?: (id: string) => string;
}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const live = useRef<DragState | null>(null);
  const cb = useRef({ carry, onDrop, scroller, lockBand });
  cb.current = { carry, onDrop, scroller, lockBand };
  /** The floating card. Moved by hand on every pointer move: re-rendering the
   *  whole page 60 times a second to shift one fixed element made large
   *  collections stutter. The page re-renders only when the drop target changes. */
  const overlayRef = useRef<HTMLDivElement>(null);
  /** Ends a drag in progress without dropping; set while one is on. */
  const abort = useRef<(() => void) | null>(null);

  const update = useCallback((next: DragState | null) => {
    live.current = next;
    setDrag(next);
  }, []);
  const follow = useCallback((x: number, y: number) => {
    const d = live.current;
    if (!d) return;
    live.current = { ...d, x, y };
    const el = overlayRef.current;
    if (el) {
      el.style.left = `${x - d.offsetX}px`;
      el.style.top = `${y - d.offsetY}px`;
    }
  }, []);
  // Leaving the page mid-drag must not leave the listeners, or the grabbing
  // cursor and the page-wide no-select, behind.
  useEffect(() => () => abort.current?.(), []);

  // Auto-scroll near the top and bottom edges — the scroller's, or the
  // window's below its top bar — re-aiming as it goes.
  useEffect(() => {
    if (!drag) return;
    let raf = 0;
    const tick = () => {
      const d = live.current;
      if (d) {
        const s = cb.current.scroller;
        const box = s?.getBoundingClientRect();
        const top = box ? box.top : (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tr-topbar-h')) || 56) + 56;
        const bottom = box ? box.bottom : window.innerHeight;
        const v = d.y < top + EDGE ? -Math.min(1, (top + EDGE - d.y) / EDGE) : d.y > bottom - EDGE ? Math.min(1, (d.y - (bottom - EDGE)) / EDGE) : 0;
        if (v) {
          (s ?? window).scrollBy(0, v * 18);
          const target = hitTest(d.x, d.y, d.target, d.lock);
          if (!sameTarget(target, d.target)) update({ ...live.current!, target });
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // Only whether a drag is on, not every move of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!drag, update]);

  const onPointerDown = useCallback(
    (id: string) => (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled || e.button !== 0 || !e.isPrimary || live.current) return;
      if ((e.target as Element).closest(NO_DRAG)) return;
      const el = e.currentTarget;
      const touch = e.pointerType === 'touch';
      const sx = e.clientX;
      const sy = e.clientY;
      let lx = sx;
      let ly = sy;
      let started = false;
      let hold: ReturnType<typeof setTimeout> | undefined;

      const start = () => {
        started = true;
        const r = el.getBoundingClientRect();
        const ids = cb.current.carry(id);
        // The floating card: a grid card keeps its width, a list row becomes a card.
        const width = el.dataset.dragWidth ? Number(el.dataset.dragWidth) : r.width;
        const offsetX = Math.min(Math.max(sx - r.left, 16), width - 16);
        // Held where it was grabbed, within the card's image (the thumb sits 14px down).
        const offsetY = el.dataset.dragWidth ? 24 : Math.min(Math.max(sy - r.top - 14, 12), width * 0.75 - 12);
        document.body.classList.add('tr-dragging');
        window.getSelection()?.removeAllRanges();
        if (touch && navigator.vibrate) navigator.vibrate(10);
        update({ ids, lead: id, x: lx, y: ly, offsetX, offsetY, width, target: null, lock: cb.current.lockBand?.(id) });
      };
      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        lx = ev.clientX;
        ly = ev.clientY;
        if (!started) {
          const dist = Math.hypot(lx - sx, ly - sy);
          if (touch) {
            if (dist > TOUCH_SLOP) end(false); // a scroll, not a hold
            return;
          }
          if (dist < MOUSE_SLOP) return;
          // Aimed at once too: a quick flick is one move, then the release.
          start();
        }
        follow(lx, ly);
        const d = live.current!;
        const target = hitTest(lx, ly, d.target, d.lock);
        if (!sameTarget(target, d.target)) update({ ...d, target });
      };
      // While a touch drag is on, the page must not scroll under the finger.
      const blockScroll = (ev: TouchEvent) => { if (started) ev.preventDefault(); };
      const key = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { ev.preventDefault(); end(false); } };
      const up = (ev: PointerEvent) => { if (ev.pointerId === e.pointerId) end(true); };
      const cancel = (ev: PointerEvent) => { if (ev.pointerId === e.pointerId) end(false); };
      const noMenu = (ev: Event) => { if (started) ev.preventDefault(); };
      // A card's thumbnail is an image, and the browser would start its own
      // drag of it a few pixels in — cancelling the pointer under us
      // (pointercancel), so the card never lifted when grabbed by its picture.
      // While this gesture is on, no native drag begins.
      const noNativeDrag = (ev: DragEvent) => { ev.preventDefault(); };
      function end(drop: boolean) {
        clearTimeout(hold);
        abort.current = null;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        window.removeEventListener('keydown', key, true);
        window.removeEventListener('contextmenu', noMenu, true);
        window.removeEventListener('dragstart', noNativeDrag, true);
        document.removeEventListener('touchmove', blockScroll);
        if (!started) return;
        const d = live.current;
        document.body.classList.remove('tr-dragging');
        update(null);
        // The release would otherwise land as a click and open the study.
        const swallow = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', swallow, true);
        setTimeout(() => window.removeEventListener('click', swallow, true), 0);
        if (drop && d?.target) cb.current.onDrop(d.ids, d.target);
      }
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
      window.addEventListener('keydown', key, true);
      window.addEventListener('contextmenu', noMenu, true);
      window.addEventListener('dragstart', noNativeDrag, true);
      document.addEventListener('touchmove', blockScroll, { passive: false });
      abort.current = () => end(false);
      if (touch) hold = setTimeout(start, TOUCH_HOLD_MS);
    },
    [enabled, update, follow],
  );

  return { drag, onPointerDown, overlayRef };
}

/**
 * Slide the studies to their new places instead of letting them jump (FLIP):
 * after each layout that changed the order, every `[data-sid]` under `root` is
 * put back where it was drawn and eased to where it now is.
 */
export function useSlide(root: React.RefObject<HTMLElement>, orderKey: string, layout: string) {
  const seen = useRef(new Map<string, { el: HTMLElement; x: number; y: number }>());
  const lastLayout = useRef(layout);
  useLayoutEffect(() => {
    const host = root.current;
    if (!host) return;
    // Switching grid ↔ list is a different page, not a reorder: nothing slides.
    if (lastLayout.current !== layout) {
      lastLayout.current = layout;
      seen.current = new Map();
    }
    const els = [...host.querySelectorAll<HTMLElement>('[data-sid]')];
    // Where each one is drawn right now (mid-slide, possibly), then where the
    // layout puts it. A place is kept relative to the host's content, so it
    // holds whatever scrolled since — the window (the collection page) or the
    // host itself (the viewer's image list).
    const drawn = els.map((el) => el.getBoundingClientRect());
    const origin = () => {
      const b = host.getBoundingClientRect();
      return { x: b.left - host.scrollLeft, y: b.top - host.scrollTop };
    };
    const o = origin();
    els.forEach((el) => { el.style.transition = 'none'; el.style.transform = ''; });
    const next = new Map<string, { el: HTMLElement; x: number; y: number }>();
    const moved: HTMLElement[] = [];
    els.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const id = el.dataset.sid!;
      next.set(id, { el, x: r.left - o.x, y: r.top - o.y });
      // The same element starts from where it is drawn; one React mounted
      // afresh (it changed band) from where its predecessor last stood.
      const was = seen.current.get(id);
      if (!was) return;
      const from = was.el === el ? { x: drawn[i].left, y: drawn[i].top } : { x: was.x + o.x, y: was.y + o.y };
      const dx = from.x - r.left;
      const dy = from.y - r.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      moved.push(el);
    });
    seen.current = next;
    if (!moved.length) return;
    // One reflow, then every slide starts together.
    void host.offsetWidth;
    moved.forEach((el) => {
      el.style.transition = 'transform 200ms cubic-bezier(.2,.7,.3,1)';
      el.style.transform = '';
    });
  }, [root, orderKey, layout]);
}
